/**
 * Proteção anti-wipe dos cards do Tempo de Corte Exato por Pontuação.
 * Aba Jornada / outras NÃO usam este DB — mas um save [] acidental apagava tudo.
 */

import { pastaBackupsRaiz } from './adeBackupAutomatico';
import { nwDisponivel } from './nwModulosJanela';

export const ARQUIVO_ULTIMO_BOM_CARDS_TC = 'ultimo-bom-cards.json';
export const PASTA_MODULO_TEMPO_CORTE = 'tempo-corte';

/** Bloqueia zerar de uma vez um acervo grande. */
export const LIMITE_WIPE_TOTAL = 5;
/** Bloqueia perder mais de 70% dos cards num único save. */
export const LIMITE_QUEDA_FRACAO = 0.3;

type NwRequire = (mod: string) => unknown;

function nwRequire(): NwRequire | null {
  try {
    const w = window as unknown as { nw?: { require?: NwRequire } };
    return w.nw?.require ?? null;
  } catch {
    return null;
  }
}

export function deveBloquearGravacaoCardsTempoCorte(
  existentes: unknown[] | null | undefined,
  novos: unknown[] | null | undefined
): { bloquear: boolean; motivo: string } {
  const nAntigo = Array.isArray(existentes) ? existentes.length : 0;
  const nNovo = Array.isArray(novos) ? novos.length : 0;

  if (nAntigo <= 0) return { bloquear: false, motivo: '' };

  if (nNovo === 0 && nAntigo >= LIMITE_WIPE_TOTAL) {
    return {
      bloquear: true,
      motivo: `Tentativa de gravar 0 cards sobre ${nAntigo} existentes (wipe bloqueado).`,
    };
  }

  if (nAntigo >= 20 && nNovo < nAntigo * LIMITE_QUEDA_FRACAO) {
    return {
      bloquear: true,
      motivo: `Queda brusca de cards (${nAntigo} → ${nNovo}) bloqueada para não perder o acervo.`,
    };
  }

  return { bloquear: false, motivo: '' };
}

/** Grava cópia de segurança dos cards na pasta Backups do NW.js (não depende do horário). */
export function salvarSnapshotUltimoBomCardsTempoCorte(cards: unknown[]): void {
  if (!Array.isArray(cards) || cards.length === 0) return;
  if (!nwDisponivel()) return;
  const req = nwRequire();
  const raiz = pastaBackupsRaiz();
  if (!req || !raiz) return;
  try {
    const fs = req('fs') as typeof import('fs');
    const path = req('path') as typeof import('path');
    const pasta = path.join(raiz, PASTA_MODULO_TEMPO_CORTE);
    if (!fs.existsSync(pasta)) fs.mkdirSync(pasta, { recursive: true });
    const payload = {
      _tipo: 'ade-tempo-corte-ultimo-bom',
      _data: new Date().toISOString(),
      quantidade: cards.length,
      dados: {
        'produtos-tempo-corte-ade': JSON.stringify(cards),
      },
      indexedDB: {
        'ade-tempo-corte-db/tempoCorte/produtosTempoCorteSalvos': cards,
      },
    };
    const dest = path.join(pasta, ARQUIVO_ULTIMO_BOM_CARDS_TC);
    fs.writeFileSync(dest, JSON.stringify(payload), 'utf8');
    // Cópia rotativa leve (só a última hora no nome) — não enche disco
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}`;
    const rotDir = path.join(pasta, 'ultimo-bom-hora');
    if (!fs.existsSync(rotDir)) fs.mkdirSync(rotDir, { recursive: true });
    fs.writeFileSync(path.join(rotDir, `${stamp}.json`), JSON.stringify(payload), 'utf8');
    // Mantém no máx. 48 arquivos (~2 dias)
    const arquivos = fs
      .readdirSync(rotDir)
      .filter((n) => n.endsWith('.json'))
      .map((n) => ({ n, t: fs.statSync(path.join(rotDir, n)).mtimeMs }))
      .sort((a, b) => b.t - a.t);
    for (const velho of arquivos.slice(48)) {
      try {
        fs.unlinkSync(path.join(rotDir, velho.n));
      } catch {
        /* ignore */
      }
    }
  } catch (e) {
    console.warn('[Sistema Ade] Falha ao gravar snapshot último-bom dos cards:', e);
  }
}

/** Preferência de leitura do caminho do snapshot. */
export function caminhoUltimoBomCardsTempoCorte(): string | null {
  const req = nwRequire();
  const raiz = pastaBackupsRaiz();
  if (!req || !raiz) return null;
  try {
    const path = req('path') as typeof import('path');
    return path.join(raiz, PASTA_MODULO_TEMPO_CORTE, ARQUIVO_ULTIMO_BOM_CARDS_TC);
  } catch {
    return null;
  }
}
